<template>
  <div
    class="row items-center"
  >
    <login-method-icon
      v-if="showIcon"
      class="q-pb-md q-mr-sm icon-width"
      :login-method="modelValue"
      centered
      hide-tooltip
    />
    <q-select
      v-bind="$attrs"
      class="selector"
      :model-value="modelValue"
      :disable="disable"
      :options="loginMethodOptions"
      :label="selectorLabel"
      :rules="selectorRules"
      lazy-rules
      map-options
      emit-value
      data-e2e="dropdown-select-login-method"
      @update:model-value="onUpdate"
    >
      <template #option="prop">
        <universal-tooltip
          :text="disabledMethodsTooltip"
          :show-tooltip="prop.opt.disable && !!(disabledMethodsTooltip)"
        >
          <q-item v-bind="prop.itemProps">
            <q-item-section>
              <q-item-label>{{ prop.opt.label }}</q-item-label>
              <q-item-label caption>
                {{ prop.opt.description }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </universal-tooltip>
      </template>
    </q-select>
  </div>
</template>

<script>
import LoginMethodIcon from 'components/UI/LoginMethodIcon.vue';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import { useAuth } from 'src/composables/auth';
import { LOGIN_METHOD, LOGIN_METHODS } from 'src/constants/authorization';

export default {
  name: 'LoginMethodSelector',

  components: { LoginMethodIcon, UniversalTooltip },

  props: {
    modelValue: {
      validator: (val) => LOGIN_METHODS.includes(val) || val === null,
      required: true,
    },

    disable: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      default: null,
    },

    /**
     * Show selected method icon
     */
    showIcon: {
      type: Boolean,
      default: false,
    },

    /**
     * Adjust rules for the provider selector
     */
    rules: {
      type: Array,
      default: () => [],
    },

    /**
     * List of login methods that should be disabled to be picked
     */
    disabledMethods: {
      type: Array,
      default: () => [],
    },

    disabledMethodsTooltip: {
      type: String,
      default: '',
    },
  },

  emits: ['update:modelValue'],

  setup() {
    const { loginMethodsNames } = useAuth();
    return { loginMethodsNames };
  },

  computed: {
    selectorLabel() {
      return this.label ?? this.$t('login.textLoginMethod');
    }, // selectorLabel()

    loginMethodOptions() {
      return [
        {
          value: LOGIN_METHOD.PASSWORD,
          label: this.loginMethodsNames[LOGIN_METHOD.PASSWORD],
          disable: this.shouldDisableLoginMethod(LOGIN_METHOD.PASSWORD),
        },
        {
          value: LOGIN_METHOD.GOOGLE,
          label: this.loginMethodsNames[LOGIN_METHOD.GOOGLE],
          disable: this.shouldDisableLoginMethod(LOGIN_METHOD.GOOGLE),
        },
        {
          value: LOGIN_METHOD.APPLE,
          label: this.loginMethodsNames[LOGIN_METHOD.APPLE],
          disable: this.shouldDisableLoginMethod(LOGIN_METHOD.APPLE),
        },
        // todo: make it optional - only if Microsoft provider is setup
        {
          value: LOGIN_METHOD.MICROSOFT,
          label: this.loginMethodsNames[LOGIN_METHOD.MICROSOFT],
          disable: this.shouldDisableLoginMethod(LOGIN_METHOD.MICROSOFT),
        },
      ];
    }, // loginMethodOptions()

    selectorRules() {
      return [
        ...(this.rules || []),
        (value) => Object.values(LOGIN_METHOD).includes(value) || this.$t('login.textSelectLoginMethod'),
      ];
    }, // rules()
  },

  methods: {
    onUpdate(value) {
      this.$emit('update:modelValue', value);
    },

    /**
     * @param {AuthMethod} loginMethod
     */
    shouldDisableLoginMethod(loginMethod) {
      return Array.isArray(this.disabledMethods)
        ? this.disabledMethods.includes(loginMethod)
        : false;
    },
  },
};
</script>

<style scoped lang="scss">
.selector {
  min-width: 125px;
}

.icon-width {
  min-width: 50px;
}
</style>
